import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import {
  Scissors, Upload, Sparkles, Loader2, Play, Film, Clock, Wand2, Check, RefreshCcw, ChevronRight, RotateCcw, Pause,
} from "lucide-react";

/** Utility: extract N keyframes from a video File client-side, return [{time, blob}]. */
async function extractKeyframes(file, count = 8) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.crossOrigin = "anonymous";
    video.src = url;

    video.addEventListener("loadedmetadata", async () => {
      const duration = isFinite(video.duration) ? video.duration : 0;
      if (!duration || duration <= 0) {
        URL.revokeObjectURL(url);
        reject(new Error("Video has no duration"));
        return;
      }
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 800 / (video.videoWidth || 800));
      canvas.width = Math.round((video.videoWidth || 800) * scale);
      canvas.height = Math.round((video.videoHeight || 450) * scale);
      const ctx = canvas.getContext("2d");
      const frames = [];
      const step = duration / (count + 1);
      for (let i = 1; i <= count; i++) {
        const t = step * i;
        await new Promise((r) => {
          const seeked = () => {
            video.removeEventListener("seeked", seeked);
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              canvas.toBlob((blob) => {
                if (blob) frames.push({ time: t, blob });
                r();
              }, "image/jpeg", 0.72);
            } catch (e) { r(); }
          };
          video.addEventListener("seeked", seeked);
          try { video.currentTime = t; } catch { r(); }
        });
      }
      URL.revokeObjectURL(url);
      resolve({ duration, frames });
    });
    video.addEventListener("error", () => {
      URL.revokeObjectURL(url);
      reject(new Error("Video load failed"));
    });
  });
}

/** Cut a segment of a video into a new .mp4 File via MediaSource-less canvas fallback.
 *  For simplicity + reliability, we hand the whole source File back to the editor and
 *  set its trimStart/trimEnd — the editor already knows how to render trimmed segments.
 */

function fmtTime(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export default function AutoClipsPanel({ onOpenClip }) {
  const [file, setFile] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [analysing, setAnalysing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [clips, setClips] = useState([]);
  const [source, setSource] = useState(null); // 'claude' or 'fallback'
  const [context, setContext] = useState("");
  const [thumbs, setThumbs] = useState({}); // clipIdx -> dataURL
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!file) return;
    const u = URL.createObjectURL(file);
    setVideoUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  const reset = () => {
    setFile(null); setClips([]); setSource(null); setThumbs({}); setDuration(0);
  };

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("video/")) {
      toast.error("Auto Clips needs a video file");
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("Video too large — try under 200 MB for browser processing");
      return;
    }
    setFile(f); setClips([]); setSource(null); setThumbs({});
  };

  const analyse = async () => {
    if (!file) return;
    setAnalysing(true); setProgress(5); setClips([]);
    try {
      // 1. Extract 8 keyframes
      const { duration: dur, frames } = await extractKeyframes(file, 8);
      setDuration(dur);
      setProgress(35);

      // 2. Send to backend
      const fd = new FormData();
      fd.append("body_json", JSON.stringify({
        total_duration_s: dur,
        keyframe_times: frames.map((f) => f.time),
        context: context.slice(0, 400),
        target_clip_length_s: 15,
        target_count: 6,
      }));
      frames.forEach((f, i) => {
        fd.append(`frame${i}`, new File([f.blob], `frame${i}.jpg`, { type: "image/jpeg" }));
      });
      setProgress(50);
      const r = await api.post("/studio/auto-clips", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setProgress(85);
      setClips(r.data?.clips || []);
      setSource(r.data?.source || null);

      // 3. Generate a thumb for each clip (middle of each range)
      const nextThumbs = {};
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.src = URL.createObjectURL(file);
      await new Promise((res) => video.addEventListener("loadedmetadata", res, { once: true }));
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 480 / (video.videoWidth || 480));
      canvas.width = Math.round((video.videoWidth || 480) * scale);
      canvas.height = Math.round((video.videoHeight || 270) * scale);
      const ctx = canvas.getContext("2d");
      for (let i = 0; i < (r.data?.clips || []).length; i++) {
        const c = r.data.clips[i];
        const mid = (c.start_s + c.end_s) / 2;
        await new Promise((res2) => {
          const s = () => {
            video.removeEventListener("seeked", s);
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              nextThumbs[i] = canvas.toDataURL("image/jpeg", 0.7);
            } catch { /* noop */ }
            res2();
          };
          video.addEventListener("seeked", s);
          try { video.currentTime = mid; } catch { res2(); }
        });
      }
      URL.revokeObjectURL(video.src);
      setThumbs(nextThumbs);
      setProgress(100);
      if (r.data?.source === "claude") {
        toast.success(`Claude found ${r.data.clips.length} clip picks`);
      } else {
        toast.info(`Suggested ${r.data.clips.length} evenly-spaced clips (AI unavailable)`);
      }
    } catch (e) {
      console.error(e);
      toast.error(formatApiError(e.response?.data?.detail) || "Auto-clip generation failed");
    } finally {
      setAnalysing(false);
      setTimeout(() => setProgress(0), 800);
    }
  };

  const openClip = (clip, idx) => {
    if (!file) return;
    // Hand the FULL source file to the editor with meta describing where to trim.
    onOpenClip(file, {
      startS: clip.start_s,
      endS: clip.end_s,
      hook: clip.hook,
      caption: clip.caption,
      hashtags: clip.hashtags,
      vibe: clip.vibe,
      title: `Auto clip · ${clip.hook || `#${idx + 1}`}`,
    });
  };

  return (
    <div className="space-y-6" data-testid="studio-auto-clips-panel">
      {!file ? (
        <div className="glass rounded-3xl p-8 md:p-12" data-testid="auto-clips-upload">
          <div className="flex items-start gap-4 mb-6">
            <div className="h-14 w-14 rounded-2xl bg-emerald-500/15 flex items-center justify-center shrink-0">
              <Scissors className="h-6 w-6 text-emerald-300" />
            </div>
            <div>
              <div className="font-display font-bold text-3xl tracking-tight mb-1">Auto clips</div>
              <p className="text-zinc-400 max-w-xl">
                Drop a long video (podcast, vlog, livestream) — Claude reads the frames and hands back
                6 draft short-form clips you can edit and post in minutes.
              </p>
            </div>
          </div>

          <label className="block">
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="auto-clips-file-input"
            />
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-white/10 rounded-2xl p-10 text-center cursor-pointer hover:border-emerald-400/40 hover:bg-emerald-500/[0.02] transition-colors"
              data-testid="auto-clips-drop-zone"
            >
              <Upload className="h-8 w-8 text-emerald-300 mx-auto mb-3" />
              <div className="font-display font-bold text-xl mb-1">Drop a long video</div>
              <div className="text-sm text-zinc-500">MP4, WebM, or MOV · under 200 MB</div>
            </div>
          </label>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Video preview + controls */}
          <div className="glass rounded-3xl p-4 md:p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl overflow-hidden bg-black aspect-video">
                {videoUrl && (
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    controls
                    className="w-full h-full object-contain"
                    onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
                    data-testid="auto-clips-preview-video"
                  />
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1.5">
                    <Film className="h-3 w-3" /> Source
                  </div>
                  <div className="font-semibold text-white truncate">{file.name}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-3 mt-0.5">
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtTime(duration)}</span>
                    <span>· {(file.size / 1024 / 1024).toFixed(1)} MB</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-2">
                    Context (optional — helps Claude pick better)
                  </label>
                  <textarea
                    value={context}
                    onChange={(e) => setContext(e.target.value.slice(0, 400))}
                    rows={3}
                    placeholder="e.g. 20-min interview about launching a skincare brand — key moment when guest reveals revenue milestone"
                    className="w-full bg-white/[0.02] border border-white/10 rounded-md p-2.5 text-white text-sm resize-none"
                    data-testid="auto-clips-context-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={analyse}
                    disabled={analysing || !duration}
                    className="nexus-gradient text-white border-0 rounded-full h-10 px-5 font-semibold disabled:opacity-60"
                    data-testid="auto-clips-analyse-btn"
                  >
                    {analysing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Reading frames…</> : <><Wand2 className="h-4 w-4 mr-2" /> Generate clips</>}
                  </Button>
                  <Button
                    onClick={reset}
                    variant="outline"
                    className="rounded-full border-white/10 text-white hover:bg-white/5 h-10"
                    data-testid="auto-clips-reset-btn"
                  >
                    <RefreshCcw className="h-4 w-4 mr-2" /> Change video
                  </Button>
                </div>
                {analysing && progress > 0 && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full nexus-gradient transition-all duration-300" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                      {progress < 40 ? "Extracting keyframes…" : progress < 85 ? "Claude reviewing frames…" : "Building thumbnails…"}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Clip picks */}
          {clips.length > 0 && (
            <div className="space-y-3" data-testid="auto-clips-results">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-nexus-pink" /> {source === "claude" ? "Claude's picks" : "Suggested picks"}
                  </div>
                  <div className="font-display font-bold text-2xl tracking-tight mt-1">
                    {clips.length} draft clip{clips.length === 1 ? "" : "s"} ready
                  </div>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3" data-testid="auto-clips-cards">
                {clips.map((c, i) => (
                  <ClipTrimCard
                    key={i}
                    idx={i}
                    clip={c}
                    thumb={thumbs[i]}
                    videoUrl={videoUrl}
                    duration={duration}
                    onChange={(next) => setClips((prev) => prev.map((row, j) => (j === i ? { ...row, ...next } : row)))}
                    onOpen={() => openClip(clips[i], i)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Inline mini-scrubber for a single Auto-Clip pick — draggable IN/OUT handles + inline preview. */
function ClipTrimCard({ idx, clip, thumb, videoUrl, duration, onChange, onOpen }) {
  const [previewing, setPreviewing] = useState(false);
  const previewRef = useRef(null);
  const railRef = useRef(null);
  const [dragging, setDragging] = useState(null); // 'start' | 'end' | null

  // Original picks so we can Reset
  const originalRef = useRef({ start: clip.start_s, end: clip.end_s });

  const clipDur = Math.max(0, clip.end_s - clip.start_s);
  const startPct = duration > 0 ? (clip.start_s / duration) * 100 : 0;
  const endPct = duration > 0 ? (clip.end_s / duration) * 100 : 100;

  const clampNext = (nextStart, nextEnd) => {
    const MIN = 3;      // min clip length
    const MAX = 90;     // max clip length
    let s = Math.max(0, Math.min(nextStart, duration - MIN));
    let e = Math.max(s + MIN, Math.min(nextEnd, duration));
    if (e - s > MAX) {
      if (nextEnd === clip.end_s) s = e - MAX; // dragging start
      else e = s + MAX;
    }
    return { start_s: Number(s.toFixed(2)), end_s: Number(e.toFixed(2)) };
  };

  const onRailPointerDown = (which) => (ev) => {
    ev.preventDefault();
    setDragging(which);
  };

  useEffect(() => {
    if (!dragging || !duration) return;
    const rail = railRef.current;
    if (!rail) return;
    const move = (ev) => {
      const rect = rail.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
      const t = pct * duration;
      if (dragging === "start") {
        const next = clampNext(t, clip.end_s);
        onChange(next);
      } else {
        const next = clampNext(clip.start_s, t);
        onChange(next);
      }
      // keep preview in sync
      if (previewRef.current) {
        try { previewRef.current.currentTime = dragging === "start" ? clip.start_s : clip.end_s; } catch { /* noop */ }
      }
    };
    const up = () => setDragging(null);
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, [dragging, duration, clip.start_s, clip.end_s, onChange]);

  const togglePreview = () => {
    const v = previewRef.current;
    if (!v) return;
    if (previewing) {
      v.pause();
      setPreviewing(false);
    } else {
      try { v.currentTime = clip.start_s; } catch { /* noop */ }
      v.play().catch(() => {});
      setPreviewing(true);
    }
  };

  // Auto-stop when the play head passes end_s
  const onTimeUpdate = () => {
    const v = previewRef.current;
    if (!v || !previewing) return;
    if (v.currentTime >= clip.end_s - 0.05) {
      v.pause();
      setPreviewing(false);
      try { v.currentTime = clip.start_s; } catch { /* noop */ }
    }
  };

  const reset = () => {
    onChange({ start_s: originalRef.current.start, end_s: originalRef.current.end });
  };

  const isChanged = clip.start_s !== originalRef.current.start || clip.end_s !== originalRef.current.end;

  return (
    <div className="glass rounded-2xl overflow-hidden hover:border-nexus-pink/40 transition-colors" data-testid={`auto-clip-${idx}`}>
      <div className="aspect-video bg-black relative overflow-hidden">
        {videoUrl ? (
          <video
            ref={previewRef}
            src={videoUrl}
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={() => { try { previewRef.current.currentTime = clip.start_s; } catch { /* noop */ } }}
            data-testid={`auto-clip-preview-${idx}`}
          />
        ) : thumb ? (
          <img src={thumb} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600"><Film className="h-8 w-8" /></div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest">
            {fmtTime(clip.start_s)} → {fmtTime(clip.end_s)}
          </span>
          <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest text-emerald-300">
            {clipDur.toFixed(0)}s
          </span>
        </div>
        <button
          onClick={togglePreview}
          className="absolute inset-0 flex items-center justify-center bg-black/25 hover:bg-black/45 transition-colors text-white opacity-0 hover:opacity-100 focus:opacity-100"
          data-testid={`auto-clip-preview-toggle-${idx}`}
          aria-label={previewing ? "Pause preview" : "Play preview"}
        >
          {previewing ? <Pause className="h-8 w-8" /> : <Play className="h-8 w-8" />}
        </button>
      </div>

      <div className="p-4 space-y-3">
        <div>
          <div className="font-display font-bold text-lg tracking-tight truncate">
            {clip.hook || `Clip ${idx + 1}`}
          </div>
          <div className="text-xs text-zinc-400 line-clamp-2 min-h-[32px]">
            {clip.caption || clip.vibe || "Auto-generated clip"}
          </div>
        </div>

        {/* Mini scrubber with IN/OUT handles */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">Trim window</span>
            {isChanged && (
              <button
                onClick={reset}
                className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white flex items-center gap-1"
                data-testid={`auto-clip-reset-${idx}`}
              >
                <RotateCcw className="h-2.5 w-2.5" /> Reset
              </button>
            )}
          </div>
          <div
            ref={railRef}
            className="relative h-6 rounded-full bg-white/[0.04] border border-white/10 select-none touch-none"
            data-testid={`auto-clip-rail-${idx}`}
          >
            {/* Selected window highlight */}
            <div
              className="absolute top-0 bottom-0 nexus-gradient rounded-full opacity-70"
              style={{ left: `${startPct}%`, width: `${Math.max(2, endPct - startPct)}%` }}
            />
            {/* Start handle */}
            <div
              onPointerDown={onRailPointerDown("start")}
              className="absolute top-1/2 -translate-y-1/2 h-8 w-3 rounded-full bg-white cursor-ew-resize shadow-lg border border-nexus-pink"
              style={{ left: `calc(${startPct}% - 6px)` }}
              data-testid={`auto-clip-handle-start-${idx}`}
              aria-label="Start handle"
            />
            {/* End handle */}
            <div
              onPointerDown={onRailPointerDown("end")}
              className="absolute top-1/2 -translate-y-1/2 h-8 w-3 rounded-full bg-white cursor-ew-resize shadow-lg border border-nexus-blue"
              style={{ left: `calc(${endPct}% - 6px)` }}
              data-testid={`auto-clip-handle-end-${idx}`}
              aria-label="End handle"
            />
          </div>
          <div className="flex justify-between text-[10px] text-zinc-500 mt-1 tabular-nums">
            <span>0:00</span>
            <span>{fmtTime(duration)}</span>
          </div>
        </div>

        {clip.hashtags?.length > 0 && (
          <div className="text-[10px] text-nexus-blue flex flex-wrap gap-1">
            {clip.hashtags.slice(0, 6).map((h, j) => <span key={j}>{h}</span>)}
          </div>
        )}

        <Button
          onClick={onOpen}
          className="w-full nexus-gradient text-white border-0 rounded-full h-9 font-semibold"
          data-testid={`auto-clip-open-${idx}`}
        >
          <Play className="h-3.5 w-3.5 mr-1.5" /> Open in editor
        </Button>
      </div>
    </div>
  );
}
